import React from 'react';
import { Trophy, Zap, Star, Crown } from 'lucide-react';

// Score: purchase price weighted by lead time (+2 % per day) — lower is better
export const rankSuppliers = (links) => {
  const priced = links.filter(l => l.purchase_price !== '' && l.purchase_price != null && !Number.isNaN(Number(l.purchase_price)) && (parseInt(l.stock, 10) || 0) > 0);
  if (!priced.length) return { cheapest: null, fastest: null, best: null, minPrice: null };
  const price = (l) => Number(l.purchase_price);
  const lead = (l) => parseInt(l.lead_time_days, 10) || 0;
  const cheapest = priced.reduce((a, b) => (price(b) < price(a) ? b : a));
  const fastest = priced.reduce((a, b) => (lead(b) < lead(a) || (lead(b) === lead(a) && price(b) < price(a)) ? b : a));
  const best = priced.reduce((a, b) => (price(b) * (1 + 0.02 * lead(b)) < price(a) * (1 + 0.02 * lead(a)) ? b : a));
  return { cheapest, fastest, best, minPrice: price(cheapest) };
};

// Admin: compare a product's suppliers and pick the primary source
const SupplierComparator = ({ links, primaryId, onPrimaryChange }) => {
  if (links.length < 1) return null;
  const { cheapest, fastest, best, minPrice } = rankSuppliers(links);
  return (
    <div className="mt-3 border rounded-lg overflow-x-auto bg-white" data-testid="supplier-comparator">
      <table className="w-full text-sm min-w-[640px]">
        <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
          <tr>
            <th className="px-3 py-2 text-left">Fournisseur</th>
            <th className="px-3 py-2 text-right">PA HT</th>
            <th className="px-3 py-2 text-right">Écart</th>
            <th className="px-3 py-2 text-right">Délai</th>
            <th className="px-3 py-2 text-right">Stock</th>
            <th className="px-3 py-2 text-left">Atouts</th>
            <th className="px-3 py-2 text-center">Principal</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {links.map(l => {
            const price = l.purchase_price === '' || l.purchase_price == null ? null : Number(l.purchase_price);
            const stock = parseInt(l.stock, 10) || 0;
            const gap = price != null && minPrice != null && minPrice > 0 ? ((price - minPrice) / minPrice) * 100 : null;
            const isPrimary = primaryId === l.supplier_id;
            return (
              <tr key={l.supplier_id} data-testid={`comparator-row-${l.supplier_id}`} className={isPrimary ? 'bg-orange-50/60' : stock === 0 ? 'text-gray-400' : ''}>
                <td className="px-3 py-2 font-medium text-gray-900">{l.supplier_name || l.supplier_id}{stock === 0 && <span className="ml-2 text-[10px] text-red-600 font-semibold">rupture</span>}</td>
                <td className="px-3 py-2 text-right">{price != null ? `${price.toFixed(2)} €` : <span className="text-gray-400">—</span>}</td>
                <td className="px-3 py-2 text-right">{gap == null ? <span className="text-gray-400">—</span> : gap === 0 ? <span className="text-green-700 font-medium">réf.</span> : <span className="text-red-600">+{gap.toFixed(0)} %</span>}</td>
                <td className="px-3 py-2 text-right">{l.lead_time_days ?? '—'} j</td>
                <td className="px-3 py-2 text-right">{stock}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {best && best.supplier_id === l.supplier_id && <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full" data-testid={`badge-best-${l.supplier_id}`}><Star size={11} /> Meilleur choix</span>}
                    {cheapest && cheapest.supplier_id === l.supplier_id && <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded-full" data-testid={`badge-cheapest-${l.supplier_id}`}><Trophy size={11} /> Le moins cher</span>}
                    {fastest && fastest.supplier_id === l.supplier_id && <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full" data-testid={`badge-fastest-${l.supplier_id}`}><Zap size={11} /> Le plus rapide</span>}
                  </div>
                </td>
                <td className="px-3 py-2 text-center">
                  <button type="button" data-testid={`set-primary-${l.supplier_id}`} onClick={() => onPrimaryChange(isPrimary ? null : l.supplier_id)} title={isPrimary ? 'Retirer le statut principal' : 'Définir comme fournisseur principal'}
                    className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border ${isPrimary ? 'bg-orange-500 text-white border-orange-500' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                    <Crown size={12} /> {isPrimary ? 'Principal' : 'Choisir'}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="px-3 py-2 text-[11px] text-gray-500">Le fournisseur principal sert de référence pour la marge, le prix conseillé, le réassort et le prélèvement du stock à la commande. Sans choix, le moins cher est utilisé. « Meilleur choix » = prix pondéré par le délai (+2 % par jour), fournisseurs en stock uniquement.</p>
    </div>
  );
};

export default SupplierComparator;
